import { useEffect, useState } from 'react';
import type { useWallet } from '@solana/wallet-adapter-react';
import { toast } from 'sonner';
import type { TRPC } from '../../../contexts/TRPC';
import { getErrorMessage } from '../../../utils/errorHandling';
import { captureAndReportError } from '../../../utils/errorReporting';
import { ArDriveProvider } from '../../../utils/upload-providers';
import { createArDriveWalletAdapter } from '../../../utils/wallet';

export interface UseArDriveTopUpArgs {
  wallet: ReturnType<typeof useWallet>;
  trpc: TRPC;
  arToUsdRate: number | null;
  setArToUsdRate: (value: number | null) => void;
  checkArDriveBalance: () => Promise<void>;
}

export interface UseArDriveTopUpResult {
  showArDriveTopUpModal: boolean;
  setShowArDriveTopUpModal: (open: boolean) => void;
  topUpAmount: string;
  setTopUpAmount: (value: string) => void;
  isToppingUp: boolean;
  solToUsdRate: number | null;
  handleArDriveTopUp: () => Promise<void>;
}

export function useArDriveTopUp({
  wallet,
  trpc,
  arToUsdRate,
  setArToUsdRate,
  checkArDriveBalance,
}: UseArDriveTopUpArgs): UseArDriveTopUpResult {
  const [showArDriveTopUpModal, setShowArDriveTopUpModal] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('0.01');
  const [isToppingUp, setIsToppingUp] = useState(false);
  const [solToUsdRate, setSolToUsdRate] = useState<number | null>(null);

  const fetchSolPrice = async () => {
    try {
      const response = await fetch(
        'https://lite-api.jup.ag/price/v3?ids=So11111111111111111111111111111111111111112',
      );
      const data = await response.json();
      const solData = data?.So11111111111111111111111111111111111111112;
      if (solData?.usdPrice) {
        setSolToUsdRate(solData.usdPrice);
      }
    } catch (error) {
      captureAndReportError('Failed to fetch SOL price:', error);
      setSolToUsdRate(200);
    }
  };

  useEffect(() => {
    if (showArDriveTopUpModal) {
      if (!arToUsdRate) {
        const fetchArRate = async () => {
          try {
            const provider = new ArDriveProvider();
            const { arToUsd } = await provider.getFiatRates();
            setArToUsdRate(arToUsd);
          } catch (error) {
            captureAndReportError('Failed to fetch AR rate:', error);
          }
        };
        fetchArRate();
      }

      fetchSolPrice();
    }
  }, [showArDriveTopUpModal]);

  const handleArDriveTopUp = async () => {
    const amount = parseFloat(topUpAmount);
    if (Number.isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (amount < 0.005) {
      toast.error('Minimum top-up amount is 0.005 SOL');
      return;
    }

    if (amount > 1) {
      toast.error('Maximum top-up amount is 1 SOL');
      return;
    }

    if (!wallet.publicKey || !wallet.signMessage) {
      toast.error('Please connect your wallet first');
      return;
    }

    setIsToppingUp(true);
    toast.info('Preparing top-up transaction...');

    try {
      const provider = new ArDriveProvider();
      const walletAdapter = createArDriveWalletAdapter(wallet, {
        throwOnInvalid: false,
      });

      if (!walletAdapter) {
        toast.error('Please connect your wallet first');
        return;
      }

      await provider.initialize({
        walletAdapter,
        trpcClient: trpc.client,
      });

      const { txId } = await provider.topUpBalance(amount, trpc.client);

      toast.success('Top-up successful!', {
        description: `Transaction ID: ${txId}. Credits will be available shortly.`,
        duration: 8000,
      });

      setShowArDriveTopUpModal(false);

      setTimeout(() => {
        toast.info('Refreshing balance...');
        checkArDriveBalance();
      }, 5000);
    } catch (error) {
      captureAndReportError('Failed to top up ArDrive balance:', error);
      toast.error(`Failed to top up balance: ${getErrorMessage(error)}`);
    } finally {
      setIsToppingUp(false);
    }
  };

  return {
    showArDriveTopUpModal,
    setShowArDriveTopUpModal,
    topUpAmount,
    setTopUpAmount,
    isToppingUp,
    solToUsdRate,
    handleArDriveTopUp,
  };
}
